import { useState, useEffect } from "react";
import { colors } from "../theme";

export interface WaitlistPresets {
  interest?: string[];
  betaTester?: boolean;
  contactMe?: boolean;
}

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
  presets?: WaitlistPresets;
}

export function WaitlistModal({
  isOpen,
  onClose,
  isDark,
  presets,
}: WaitlistModalProps) {
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState<string[]>(presets?.interest ?? []);
  const [betaTester, setBetaTester] = useState(presets?.betaTester ?? false);
  const [contactMe, setContactMe] = useState(presets?.contactMe ?? false);
  const [dataSize, setDataSize] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Update state when presets change (modal opens with different presets)
  useEffect(() => {
    if (isOpen) {
      setInterest(presets?.interest ?? []);
      setBetaTester(presets?.betaTester ?? false);
      setContactMe(presets?.contactMe ?? false);
      setSubmitted(false);
      setEmail("");
      setDataSize("");
    }
  }, [isOpen, presets]);

  if (!isOpen) return null;

  const handleInterestToggle = (value: string) => {
    setInterest((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Submit to some kind of backend.
    console.log({ email, interest, betaTester, contactMe, dataSize });
    setSubmitted(true);
  };

  const inputStyle = {
    backgroundColor: isDark ? colors.base02 : colors.base2,
    border: `1px solid ${isDark ? colors.base01 : colors.base1}30`,
    color: isDark ? colors.base0 : colors.base00,
  };

  const labelStyle = {
    color: isDark ? colors.base01 : colors.base1,
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 transition-colors duration-[2000ms]"
        style={{
          backgroundColor: isDark ? `${colors.base03}E6` : `${colors.base3}E6`,
          backdropFilter: "blur(8px)",
        }}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg rounded-2xl p-8 transition-colors duration-[2000ms]"
        style={{
          backgroundColor: isDark ? colors.base03 : colors.base3,
          border: `1px solid ${isDark ? colors.base02 : colors.base2}`,
          boxShadow: isDark
            ? `0 24px 48px ${colors.base03}80`
            : `0 24px 48px ${colors.base00}20`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full transition-colors"
          style={{
            backgroundColor: isDark ? colors.base02 : colors.base2,
            color: isDark ? colors.base01 : colors.base1,
          }}
        >
          ×
        </button>

        {submitted ? (
          <div className="text-center py-8">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{
                backgroundColor: isDark
                  ? `${colors.cyan}20`
                  : `${colors.blue}20`,
              }}
            >
              <span
                className="text-2xl"
                style={{ color: isDark ? colors.cyan : colors.blue }}
              >
                ✓
              </span>
            </div>
            <h2
              className="text-2xl font-semibold mb-2"
              style={{ color: isDark ? colors.base0 : colors.base00 }}
            >
              You're on the list
            </h2>
            <p style={{ color: isDark ? colors.base01 : colors.base1 }}>
              We'll be in touch soon.
            </p>
          </div>
        ) : (
          <>
            <h2
              className="text-2xl font-semibold mb-2"
              style={{ color: isDark ? colors.base0 : colors.base00 }}
            >
              Join the Waitlist
            </h2>
            <p
              className="mb-6"
              style={{ color: isDark ? colors.base01 : colors.base1 }}
            >
              Get early access to Idle Grove.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={labelStyle}
                >
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-colors"
                  style={inputStyle}
                />
              </div>

              {/* Interest */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={labelStyle}
                >
                  What are you interested in?
                </label>
                <div className="flex flex-wrap gap-2">
                  {["Cloud Storage", "Colo / On-Prem", "Both"].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleInterestToggle(option)}
                      className="px-4 py-2 rounded-full text-sm transition-all"
                      style={{
                        backgroundColor: interest.includes(option)
                          ? isDark
                            ? colors.cyan
                            : colors.blue
                          : isDark
                            ? colors.base02
                            : colors.base2,
                        color: interest.includes(option)
                          ? isDark
                            ? colors.base03
                            : colors.base3
                          : isDark
                            ? colors.base01
                            : colors.base1,
                        border: `1px solid ${interest.includes(option)
                            ? "transparent"
                            : isDark
                              ? colors.base01
                              : colors.base1
                          }30`,
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Data Size */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={labelStyle}
                >
                  How much data under management?
                </label>
                <select
                  value={dataSize}
                  onChange={(e) => setDataSize(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-colors appearance-none cursor-pointer"
                  style={inputStyle}
                >
                  <option value="">Select an option</option>
                  <option value="<100TB">Less than 100 TB</option>
                  <option value="100TB-1PB">100 TB – 1 PB</option>
                  <option value="1PB-10PB">1 PB – 10 PB</option>
                  <option value="10PB+">10 PB+</option>
                  <option value="unsure">Not sure yet</option>
                </select>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <label
                  className="flex items-center gap-3 cursor-pointer"
                  style={{ color: isDark ? colors.base0 : colors.base00 }}
                >
                  <input
                    type="checkbox"
                    checked={betaTester}
                    onChange={(e) => setBetaTester(e.target.checked)}
                    className="w-4 h-4 rounded cursor-pointer"
                    style={{ accentColor: isDark ? colors.cyan : colors.blue }}
                  />
                  <span className="text-sm">
                    I'd like to be considered for the beta program
                  </span>
                </label>

                <label
                  className="flex items-center gap-3 cursor-pointer"
                  style={{ color: isDark ? colors.base0 : colors.base00 }}
                >
                  <input
                    type="checkbox"
                    checked={contactMe}
                    onChange={(e) => setContactMe(e.target.checked)}
                    className="w-4 h-4 rounded cursor-pointer"
                    style={{ accentColor: isDark ? colors.cyan : colors.blue }}
                  />
                  <span className="text-sm">
                    I'm open to being contacted by the team
                  </span>
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="w-full px-6 py-4 rounded-full font-medium transition-all duration-300 hover:scale-[1.02]"
                style={{
                  backgroundColor: colors.magenta,
                  color: colors.base3,
                  boxShadow: `0 4px 20px ${colors.magenta}40`,
                }}
              >
                Join Waitlist
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
